import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useBalanceConfig } from '@features/balanceLedger'
import { useIsFetchingNewParams, useIsFetchingNewPeriod } from '@features/expertReports'

import { defaultStaleTime } from '../../../config/queryClient'
import type { CreditorBalancePagination, FetchCreditorBalanceSummaryParams } from '../query-api'
import type { SuppliersBalance, SuppliersBalanceTotals } from '../types/SuppliersBalance'
import { normalizeCreditorBalanceTotals } from '../utils/normalizeCreditorBalanceSummary'

interface UseFetchCreditorBalanceSummaryResult {
  data: SuppliersBalance[]
  pagination?: CreditorBalancePagination
  totals?: SuppliersBalanceTotals
  isLoading: boolean
  // True when keepPreviousData is serving stale rows because a non-page param (period/filters/search) changed.
  // Page-only changes keep this false. See useIsFetchingNewParams in @features/expertReports.
  isFetchingNewParams: boolean
  isFetchingNewPeriod: boolean
  isError: boolean
}

export const useFetchCreditorBalanceSummary = ({
  organizationId,
  period,
  page,
  pageSize,
  sortBy,
  sortDirection,
  search,
  contactIds,
  balanceDirection,
}: FetchCreditorBalanceSummaryParams): UseFetchCreditorBalanceSummaryResult => {
  const config = useBalanceConfig()
  const identityParams = { organizationId, period, sortBy, sortDirection, search, contactIds, balanceDirection }
  const queryProps: FetchCreditorBalanceSummaryParams = { ...identityParams, page, pageSize }

  const { data, isLoading, isIdle, isError, isPreviousData } = useQuery(
    [config.querySummaryKey, queryProps],
    () => config.fetchSummary(queryProps),
    {
      enabled: !!organizationId && !!period,
      staleTime: defaultStaleTime,
      keepPreviousData: true,
    },
  )

  const rows = useMemo(
    () => (data?.contacts ?? []).map(config.normalizeSummaryContact),
    [config.normalizeSummaryContact, data?.contacts],
  )
  const totals = useMemo(() => (data?.totals ? normalizeCreditorBalanceTotals(data.totals) : undefined), [data?.totals])

  const isFetchingNewParams = useIsFetchingNewParams(identityParams, isPreviousData)
  const isFetchingNewPeriod = useIsFetchingNewPeriod(period, isPreviousData)
  const shouldHidePreviousPeriod = isError && isFetchingNewPeriod

  return {
    data: shouldHidePreviousPeriod ? [] : rows,
    pagination: shouldHidePreviousPeriod ? undefined : data?.pagination,
    totals: shouldHidePreviousPeriod ? undefined : totals,
    isLoading: isIdle || isLoading,
    isFetchingNewParams,
    isFetchingNewPeriod: isFetchingNewPeriod && !isError,
    isError,
  }
}
